import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';

interface GroceryToastState {
  itemCount: number;
  addedIds: string[]; // item IDs added in this batch (currently unused by UI, kept for future)
  batchId: string; // ID passed to removeItemsByBatchId for undo
}

/**
 * Du Chef confirming something that happened on a screen the cook has already
 * left. Held here and drawn by the (app) layout, so it outlives the screen:
 * a recipe page's own toast unmounted with it the moment a delete navigated
 * away, and the cook was never told the recipe was gone (#793).
 */
export interface ChefToastState {
  message: string;
  /** Distinguishes two identical messages in a row, so the second still shows. */
  key: string;
}

interface UIState {
  groceryToast: GroceryToastState | null;
  chefToast: ChefToastState | null;
}

const initialState: UIState = {
  groceryToast: null,
  chefToast: null,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    showGroceryToast: (state, action: PayloadAction<GroceryToastState>) => {
      state.groceryToast = action.payload;
    },
    hideGroceryToast: (state) => {
      state.groceryToast = null;
    },
    showChefToast: (state, action: PayloadAction<ChefToastState>) => {
      state.chefToast = action.payload;
    },
    hideChefToast: (state, action: PayloadAction<string | undefined>) => {
      // A late timer from an earlier toast must not close the one now showing.
      if (action.payload && state.chefToast?.key !== action.payload) return;
      state.chefToast = null;
    },
  },
});

export const { showGroceryToast, hideGroceryToast, showChefToast, hideChefToast } = uiSlice.actions;

export const selectGroceryToast = (state: RootState) => state.ui.groceryToast;
export const selectChefToast = (state: RootState) => state.ui.chefToast;

export default uiSlice.reducer;
